import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest
} from '@angular/common/http';
import { Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, throwError } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { AuthenticationService } from '../services/authentication.service';
import { ErrorDialogService } from '../services/error-dialog.service';
import { AppConfig } from './app-config';

@Injectable()
export class AuthInterceptor implements HttpInterceptor {
  private inflightAuthRequest: Observable<any> = null;
  private inflightTr069AuthRequest: Observable<any> = null;

 
  constructor(
    private router: Router,
    private config: AppConfig,
    private injector: Injector,
    private errorDialogService: ErrorDialogService
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    const authService = this.injector.get(AuthenticationService);

    // Choose which token to add based on URL or other logic
    if (request.url.includes(this.config.tr069Url)) {
      request = this.addTr069Token(request);
    } else {
      request = this.addAuthToken(request);
    }

    return next.handle(request).pipe(
      catchError(error => {
        if (error instanceof HttpErrorResponse) {
          if (error.status === 401) {
            if (request.url.includes(this.config.tr069Url)) {
              // Handle TR-069 token refresh
              if (!this.inflightTr069AuthRequest) {
                this.inflightTr069AuthRequest = this.refreshTr069Token();
              }
              return this.inflightTr069AuthRequest.pipe(
                switchMap((newToken: string) => {
                  this.inflightTr069AuthRequest = null;
                  const authReq = this.addTr069Token(request);
                  return next.handle(authReq);
                }),
                catchError(err => {
                  this.logout();
                  return throwError(() => err);
                })
              );
            } else {
              // Handle main token refresh
              if (!this.inflightAuthRequest) {
                this.inflightAuthRequest = authService.refreshToken();
              }
              return this.inflightAuthRequest.pipe(
                switchMap((newToken: string) => {
                  this.inflightAuthRequest = null;
                  const authReq = this.addAuthToken(request);
                  return next.handle(authReq);
                }),
                catchError(err => {
                  this.logout();
                  return throwError(() => err);
                })
              );
            }
          } else if (error.status === 400) {
            if (error.error instanceof Blob && error.error.type === 'application/json') {
              return new Promise<any>((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (e: Event) => {
                  try {
                    // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
                    const errmsg = JSON.parse((<any>e.target).result);
                    let messages = this.getErrorMessage(errmsg);
                    this.errorDialogService.openDialog(messages);
                    reject(new HttpErrorResponse({
                      error: errmsg,
                      headers: error.headers,
                      status: error.status,
                      statusText: error.statusText,
                      url: error.url
                    }));
                  } catch {
                    reject(error);
                  }
                };
                reader.onerror = () => reject(error);
                reader.readAsText(error.error);
              });
            }
            const messages = this.getErrorMessage(error);
            this.errorDialogService.presentToast(messages);
           return throwError({
            error: { detail: messages },
            status: 400,
            statusText: 'Bad Request',
            url: error.url || ''
});
      
          } else {
            const messages = this.getErrorMessage(error);
            this.errorDialogService.openDialog(messages);
            return throwError(() => error);
          }
        }
        return throwError(() => error);
      })
    );
  }

  logout() {
    localStorage.clear();
    this.router.navigate(['/auth']);
  }

  addAuthToken(request: HttpRequest<any>): HttpRequest<any> {
    const token = localStorage.getItem('token');
    const ignoreUrls = ['refresh', 'obtain', 'accountrecovery', 'token/client'];

    if (ignoreUrls.some(u => request.url.includes(u))) {
      return request;
    }

    if (token) {
      return request.clone({
        setHeaders: {
          Authorization: `Bearer ${token}`
        }
      });
    }

    return request;
  }

  addTr069Token(request: HttpRequest<any>): HttpRequest<any> {
    const tr069Token = localStorage.getItem('tr069_access_token');
    if (tr069Token) {
      return request.clone({
        setHeaders: {
          Authorization: `${tr069Token}`
        }
      });
    }
    return request;
  }

  refreshTr069Token(): Observable<string> {
    const authService = this.injector.get(AuthenticationService);
    // You can implement this according to your API for refreshing TR-069 tokens
    // For example:
    return authService.refreshTr069Token().pipe(
      switchMap((newToken: string) => {
        if (newToken) {
          localStorage.setItem('tr069_token', newToken);
          return new Observable<string>(observer => {
            observer.next(newToken);
            observer.complete();
          });
        } else {
          this.logout();
          return throwError(() => new Error('Unable to refresh TR-069 token'));
        }
      })
    );
  }

  getErrorMessage(error: HttpErrorResponse | any): string[] {
    const errorMessage: string[] = [];
    if (error.status !== 500 && error.error && typeof error.error !== 'string') {
      const errors = Object.values(error.error);
      if (errors) {
        for (const messages of errors) {
          if (typeof messages === 'string') {
            errorMessage.push(messages);
          } else if (Array.isArray(messages)) {
            errorMessage.push(...messages);
          }
        }
      }
    }

    if (error.status === 500 && typeof error.error !== 'string') {
      if ('detail' in error.error) {
        errorMessage.push(error.error['detail']);
      } else if ('null' in error.error) {
        errorMessage.push(error.error['null']);
      }
    }

    if ('null' in error) {
      errorMessage.push(error['null']);
    }

    if (errorMessage.length === 0) {
      errorMessage.push('One or more errors occurred. Please contact administrator.');
    }

    return errorMessage;
  }
}
